import { useState } from "react";
import { ERROR_MESSAGES } from "../../constant/constant.js";
import { useAuth } from "../../components/context/LoginContext/AuthContext";

/**
 * @typedef {Object} KidPayload
 * @property {string} fName - First name of the kid
 * @property {string} lName - Last name of the kid
 * @property {string|number} age - Age of the kid
 * @property {string} [email] - Email (when age >= 13)
 * @property {string} [username] - Username (when age < 13)
 * @property {string} [password] - Password (when age < 13)
 */

/**
 * @typedef {Object} StoreKidData
 * @property {KidPayload[]} kids - Array of kids to register
 */

/**
 * @typedef {Object} StoreKidResponse
 * @property {string} message
 * @property {{ id: number }[]} kids - Array of created kids with IDs
 */

/**
 * @typedef {Object.<string, string[]>} FieldErrors
 * Keyed by field path (e.g. "kids.0.fName"), value is array of error messages.
 */

/**
 * Custom hook to store (register) kids for a parent.
 * Calls POST /parent/store-kid.
 */
export const useStoreKid = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  /** @type {[StoreKidResponse|null, Function]} */
  const [data, setData] = useState(null);
  /** @type {[FieldErrors|null, Function]} */
  const [fieldErrors, setFieldErrors] = useState(null);

  const { fetchWithAuth } = useAuth();
  const apiUrl = process.env.REACT_APP_API_URL;

  /**
   * Registers kids for the current parent.
   * @param {StoreKidData} kidData - The kids payload
   * @returns {Promise<StoreKidResponse|null>} The response data on success, null on failure
   */
  const storeKid = async (kidData) => {
    setLoading(true);
    setError("");
    setData(null);
    setFieldErrors(null);

    try {
      const response = await fetchWithAuth(`${apiUrl}/parent/store-kid`, {
        method: "POST",
        body: JSON.stringify(kidData),
      });

      const responseData = await response.json();

      if (!response.ok) {
        if (responseData?.errors) {
          setFieldErrors(responseData.errors);
        }
        const errorMsg =
          responseData?.message ||
          ERROR_MESSAGES?.UNKNOWN ||
          "Something went wrong";
        throw new Error(errorMsg);
      }

      setData(responseData);
      return responseData;
    } catch (err) {
      setError(
        err?.message || ERROR_MESSAGES?.UNKNOWN || "Something went wrong",
      );
      return null;
    } finally {
      setLoading(false);
    }
  };

  return {
    storeKid,
    loading,
    error,
    fieldErrors,
    data,
    setError,
    setFieldErrors,
  };
};
